import React, { useState, useMemo, useEffect, useRef, ReactNode } from 'react';
import { useSetRecoilState, useRecoilValue, useRecoilState } from 'recoil';
import { Config } from '@/config';
import {
  errorModalTextRecoil,
  completeModalRecoil,
  deleteModalRecoil,
  facilitiesAddModalRecoil,
} from '@/atom/ModalAtom';
import { facilitiesListRecoil } from '@/atom/FacilitiesAtom';
import { siteListRecoil } from '@/atom/SiteAtom';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import LS from '@/styles/layoutStyled';
import CS from '@/styles/componentStyled';
import BS from '@/styles/btnStyled';
import IS from '@/styles/inputStyled';

import TitleTextBox from '@/component/TitleTextBox';
import Pagenation from '@/component/Pagenation';
import SearchBtn from '@/component/SearchBtn';
import DeleteModal from '@/modal/DeleteModal';
import { hangjungdong } from '@/data/hangjungdong';
import FacilitiesAddModal from '@/modal/FacilitiesAddModal';
import { FacilityApi } from '@/sdk/FacilityApi';
import { SiteApi } from '@/sdk/SiteApi';
import {
  FacilityControllerGetFacilitiesParams,
  GetFacilitiesDtoRx,
  DeleteFacilityDtoTx,
  SiteControllerGetSitesParams,
} from '@/sdk/data-contracts';

const Facilities = () => {
  const siteApi = new SiteApi({ baseURL: Config.API_URL });
  const facilityApi = new FacilityApi({ baseURL: Config.API_URL });
  const navigate = useNavigate();

  const setErrorModalText = useSetRecoilState(errorModalTextRecoil);
  const setCompleteModalState = useSetRecoilState(completeModalRecoil);
  const [deleteModalState, setDeleteModalState] =
    useRecoilState(deleteModalRecoil);
  const [facilitiesListState, setFacilitiesListState] =
    useRecoilState(facilitiesListRecoil);
  const [facilitiesAddModalState, setFacilitiesAddModalState] = useRecoilState(
    facilitiesAddModalRecoil,
  );
  const [siteListState, setSiteListState] = useRecoilState(siteListRecoil);

  const [number, setNumber] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [facilityManagerName, setFacilityManagerName] = useState<string>('');
  const [siteName, setSiteName] = useState<string>('');
  const [condition, setCondition] = useState<string>('');
  const [sido, setSido] = useState<string>('');
  const [sigungu, setSigungu] = useState<string>('');
  const [address, setAddress] = useState<string>('');
  const [offset, setOffset] = useState<number>(0);
  const [limit, setLimit] = useState<number>(10);
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [order, setOrder] = useState<string>('desc');
  const [checkItems, setCheckItems] = useState<number[]>([]);
  const [listLength, setListLength] = useState<number>(0);

  const allCheckBtnRef = useRef<HTMLInputElement>(null);

  const downloadCSV = async (saveFileName: string) => {
    let allList: GetFacilitiesDtoRx[] | undefined;

    const dto: FacilityControllerGetFacilitiesParams = {
      sortBy,
      order,
    };

    try {
      const { data } = await facilityApi.facilityControllerGetFacilities(dto, {
        headers: {
          Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
          'Content-Type': 'application/json',
        },
      });
      if (data.results) allList = data.results;
    } catch (err: any) {}

    if (!allList || allList.length === 0) {
      setErrorModalText('다운로드할 데이터가 없습니다.');
      return;
    }

    const headers = [
      'No',
      '시설번호',
      '시설명',
      '주소',
      '시설 담당자',
      '사이트',
      '건물 상태',
      '디바이스',
    ];

    const rows = allList.map((list, index) => [
      index + 1,
      list.number,
      list.name,
      list.address,
      list.facilityManagerName.replace(', ', ' / '),
      list.siteName,
      list.condition,
      list.deviceCount,
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.join(',')),
    ].join('\n');

    const bom = '\uFEFF';
    const csvWithBom = bom + csvContent;

    const blob = new Blob([csvWithBom], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${saveFileName}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const fetchSiteList = async () => {
    const dto: SiteControllerGetSitesParams = {
      sortBy,
      order,
    };
    try {
      const { data } = await siteApi.siteControllerGetSites(dto, {
        headers: {
          Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
          'Content-Type': 'application/json',
        },
      });
      if (data.results) setSiteListState(data.results);
    } catch (err: any) {}
  };

  const filterFacilitiesList = async () => {
    const searchSido = hangjungdong.sido.find(
      (list) => list.sido === sido,
    )?.codeNm;
    const searchSiGungu = hangjungdong.sigungu.find(
      (list) => list.sido === sido && list.sigungu === sigungu,
    )?.codeNm;
    const dto: FacilityControllerGetFacilitiesParams = {
      number,
      name,
      facilityManagerName,
      siteName,
      condition,
      sido: searchSido ? searchSido : '',
      sigungu: searchSiGungu ? searchSiGungu : '',
      address,
      offset,
      limit,
      sortBy,
      order,
    };
    try {
      const { data } = await facilityApi.facilityControllerGetFacilities(dto, {
        headers: {
          Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
          'Content-Type': 'application/json',
        },
      });
      if (data.results) setFacilitiesListState(data.results);
      if (data.totalColumn) {
        setListLength(data.totalColumn);
      } else if (data.totalColumn === 0) {
        setListLength(0);
      }
    } catch (err: any) {}
  };

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCheckItems([]);
    setOffset(0);
    filterFacilitiesList();
    if (allCheckBtnRef.current) {
      allCheckBtnRef.current.checked = false;
    }
  };

  const handleLimitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setLimit(Number(e.target.value));
    setOffset(0);
  };

  const handleOffsetChange = (value: number) => {
    setOffset((value - 1) * limit);
    setCheckItems([]);
    if (allCheckBtnRef.current) {
      allCheckBtnRef.current.checked = false;
    }
  };

  const handleSingleCheck = (checked: boolean, id: number) => {
    if (checked) {
      setCheckItems((prev) => [...prev, id]);
    } else {
      setCheckItems((prev) => prev.filter((el) => el !== id));
    }
  };

  const handleAllCheck = (checked: boolean) => {
    if (checked) {
      const idArray: number[] = [];
      facilitiesListState?.forEach((el) => idArray.push(Number(el.id)));
      setCheckItems(idArray);
    } else {
      setCheckItems([]);
    }
  };

  const handleSelectDeleteNumCheck = () => {
    if (checkItems.length === 0) {
      setErrorModalText('선택된 항목이 없습니다.');
      return;
    }
    setDeleteModalState(true);
  };

  const handleSelectDelete = async () => {
    try {
      const dto: DeleteFacilityDtoTx = {
        ids: checkItems,
      };
      await facilityApi.facilityControllerDeleteFacility(dto, {
        headers: {
          Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
          'Content-Type': 'application/json',
        },
      });
      setCompleteModalState(true);
    } catch (err: any) {}
  };

  useEffect(() => {
    filterFacilitiesList();
    fetchSiteList();
  }, [limit, offset]);

  return (
    <>
      <CntBox1>
        <TitleTextBox text="시설물" />
        <form onSubmit={handleSearch}>
          <SearchOptionBox>
            <InputWrap>
              <InputArea>
                <IS.TextInputType1
                  style={{ width: 136 }}
                  type="text"
                  placeholder="시설번호"
                  value={number}
                  onChange={(e) => setNumber(e.target.value)}
                />
                <IS.TextInputType1
                  style={{ width: 136 }}
                  type="text"
                  placeholder="시설명"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                <IS.TextInputType1
                  style={{ width: 136 }}
                  type="text"
                  placeholder="시설 담당자"
                  value={facilityManagerName}
                  onChange={(e) => setFacilityManagerName(e.target.value)}
                />
                <IS.SelectType1>
                  <select
                    value={siteName}
                    onChange={(e) => setSiteName(e.target.value)}
                    className="selected"
                    style={{ width: 153 }}
                  >
                    <option value="">사이트</option>
                    {siteListState?.map((list) => (
                      <option key={list.name} value={list.name}>
                        {list.name}
                      </option>
                    ))}
                  </select>
                  <span className="img-box">
                    <img src="/img/arrow_bottom_555.svg" alt="" />
                  </span>
                </IS.SelectType1>
                <IS.TextInputType1
                  style={{ width: 136 }}
                  type="text"
                  placeholder="건물 상태"
                  value={condition}
                  onChange={(e) => setCondition(e.target.value)}
                />
              </InputArea>
              <InputArea>
                <IS.SelectType1>
                  <select
                    value={sido}
                    onChange={(e) => {
                      setSido(e.target.value);
                      setSigungu('');
                    }}
                    className="selected"
                    style={{ width: 153 }}
                  >
                    <option value="">-시/도-</option>
                    {hangjungdong.sido.map((list) => (
                      <option key={list.sido} value={list.sido}>
                        {list.codeNm}
                      </option>
                    ))}
                  </select>
                  <span className="img-box">
                    <img src="/img/arrow_bottom_555.svg" alt="" />
                  </span>
                </IS.SelectType1>
                <IS.SelectType1>
                  <select
                    value={sigungu}
                    onChange={(e) => {
                      setSigungu(e.target.value);
                    }}
                    className="selected"
                    style={{ width: 153 }}
                  >
                    <option value="">-시군구-</option>
                    {sido &&
                      hangjungdong.sigungu
                        .filter((list) => list.sido === sido)
                        .map((list) => (
                          <option key={list.sigungu} value={list.sigungu}>
                            {list.codeNm}
                          </option>
                        ))}
                  </select>
                  <span className="img-box">
                    <img src="/img/arrow_bottom_555.svg" alt="" />
                  </span>
                </IS.SelectType1>
                <IS.TextInputType1
                  style={{ width: 320 }}
                  type="text"
                  placeholder="주소"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                />
              </InputArea>
            </InputWrap>
            <SearchBtn />
          </SearchOptionBox>
        </form>
        <TableOpitonBox>
          <h1>
            전체 <span>{listLength.toLocaleString()}</span>
          </h1>
          <div className="right-box">
            <IS.SelectType1>
              <select
                value={limit}
                onChange={handleLimitChange}
                className="selected"
                style={{ width: 153 }}
              >
                <option value={10}>10개씩 보기</option>
                <option value={20}>20개씩 보기</option>
                <option value={30}>30개씩 보기</option>
                <option value={50}>50개씩 보기</option>
              </select>
              <span className="img-box">
                <img src="/img/arrow_bottom_555.svg" alt="" />
              </span>
            </IS.SelectType1>
            <BS.Btn1Type5
              type="button"
              onClick={() => downloadCSV('facilities_list')}
            >
              Excel 다운로드
              <span className="img-box">
                <img src="/img/ico_down_fff.svg" alt="" />
              </span>
            </BS.Btn1Type5>
            <BS.Btn1Type1
              type="button"
              onClick={() => setFacilitiesAddModalState(true)}
            >
              등록
            </BS.Btn1Type1>
            <BS.Btn1Type2 type="button" onClick={handleSelectDeleteNumCheck}>
              선택 삭제
            </BS.Btn1Type2>
          </div>
        </TableOpitonBox>
        <TableBox>
          <table>
            <thead>
              <tr>
                <th>
                  <IS.CheckBoxType1>
                    <input
                      type="checkbox"
                      id="all"
                      onChange={(e) => handleAllCheck(e.target.checked)}
                      ref={allCheckBtnRef}
                      checked={
                        checkItems.length === facilitiesListState?.length &&
                        facilitiesListState?.length !== 0
                          ? true
                          : false
                      }
                    />
                    <label htmlFor="all" className="img-box">
                      <img src="/img/ico_check_1.svg" alt="" />
                    </label>
                  </IS.CheckBoxType1>
                </th>
                <th>No</th>
                <th>시설번호</th>
                <th>시설명</th>
                <th>주소</th>
                <th>시설 담당자</th>
                <th>사이트</th>
                <th>건물 상태</th>
                <th>디바이스</th>
              </tr>
            </thead>
            <tbody>
              {facilitiesListState?.map((list, index) => (
                <tr key={list.id}>
                  <td>
                    <IS.CheckBoxType1>
                      <input
                        type="checkbox"
                        id={`cv-${index}`}
                        onChange={(e) =>
                          handleSingleCheck(e.target.checked, Number(list.id))
                        }
                        checked={
                          checkItems.includes(Number(list.id)) ? true : false
                        }
                      />
                      <label htmlFor={`cv-${index}`} className="img-box">
                        <img src="/img/ico_check_1.svg" alt="" />
                      </label>
                    </IS.CheckBoxType1>
                  </td>
                  <td>{(offset + index + 1).toLocaleString()}</td>
                  <td>{list.number}</td>
                  <td
                    className="button-text"
                    onClick={() => navigate(`/facilities/detail/${list.id}`)}
                  >
                    {list.name}
                  </td>
                  <td>{list.address}</td>
                  <td>{list.facilityManagerName}</td>
                  <td>{list.siteName}</td>
                  <td>{list.condition}</td>
                  <td>{list.deviceCount.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableBox>
        {listLength !== 0 && (
          <Pagenation
            listAllLength={listLength}
            listLimit={limit}
            onCurrentPageChange={handleOffsetChange}
          />
        )}
      </CntBox1>
      {facilitiesAddModalState && <FacilitiesAddModal />}
      {deleteModalState && <DeleteModal onConfirm={handleSelectDelete} />}
    </>
  );
};

export default Facilities;

export const CntBox1 = styled(CS.CntBoxType1)`
  width: 100%;
`;

export const SearchOptionBox = styled(CS.SearchOptionBox)`
  width: 100%;
  margin-top: 20px;
`;

export const InputWrap = styled.div`
  gap: 8px;
  display: flex;
  flex-direction: column;
`;

export const InputArea = styled.div`
  gap: 8px;
  display: flex;
  flex-wrap: wrap;
`;

export const TableOpitonBox = styled(CS.TableOpitonBox)`
  width: 100%;
  margin-top: 20px;
`;

export const TableBox = styled(CS.TableType1)`
  width: 100%;
  margin-top: 16px;
  margin-bottom: 32px;
`;
